/**
 * Quotation form & dropdown config.
 * Each section has: id, label, options[].
 * Each option: value, label; optional: options (nested), description, or section-specific fields.
 */
import { getGemaComponentOptions } from './technical_specification.js';

// const ovenMaterialOptions = [
//   { value: 'ms', label: 'MS' },
//   { value: 'ss', label: 'SS' },
// ];

const ovenOptions = [
  { value: 'powder_coating_curing_oven', label: 'Powder Coating Curing Oven' },
  { value: 'batch_type_oven', label: 'Batch Type Oven' },
  {
    value: 'wood_sublimation_oven',
    label: 'Wood Sublimation Oven',
    options: [
      { value: 'wood_sublimation_oven_plc_based', label: 'PLC Based' },
      { value: 'wood_sublimation_oven_without_plc', label: 'Without PLC' },
    ],
  },
  {
    value: 'water_dry_off',
    label: 'Water Dry Off Oven',
    options: [
      { value: 'conveyorised_camel_back_water_drying_oven', label: 'Conveyorised Camel Back Water Drying Oven' },
      { value: 'batch_type_water_dry_off_oven', label: 'Batch Type Water Dry Off Oven' },
    ],
  },
];

/** Builds a conveyor specification option for the specification dropdown (value + label + spec data). */
const conveyorSpecOption = (value, label, chainPitch, hangingPitch, horizontalRadius, verticalRadius) => ({
  value,
  label,
  chainPitch,
  hangingPitch,
  horizontalBandRadius: horizontalRadius,
  verticalBendRadius: verticalRadius,
});

const conveyorOptions = [
  {
    value: 'inclusive_5T',
    label: 'Inclusive 5T',
    subLabel: '5T Enclosed Overhead Conveyor',
    loadCapacityPerHook: '30 Kg',
    options: [
      conveyorSpecOption('inclusive_5T_1', '3" (75 mm) / 6" (150 mm)', '3" (75 mm)', '6" (150 mm)', '500 mm', '750 mm'),
      conveyorSpecOption('inclusive_5T_2', '4" (100 mm) / 8" (200 mm)', '4" (100 mm)', '8" (200 mm)', '600 mm', '750 mm'),
    ],
  },
  {
    value: 'inclusive_7T',
    label: 'Inclusive 7T',
    subLabel: '7T Enclosed Overhead Conveyor',
    loadCapacityPerHook: '60 Kg',
    options: [
      conveyorSpecOption('inclusive_7T_1', '4" (100 mm) / 8" (200 mm)', '4" (100 mm)', '8" (200 mm)', '750 mm', '800 mm'),
    ],
  },
  {
    value: 'four_wheel',
    label: 'Four Wheel',
    subLabel: 'Four Wheel Model Overhead Conveyor',
    loadCapacityPerHook: '45 Kg',
    options: [
      conveyorSpecOption('four_wheel_1', '6" / 12" (304.8 mm)', '6" (152.4 mm)', '12" (304.8 mm)', '600 mm', '1200 mm'),
      conveyorSpecOption('four_wheel_2', '8" / 16" (406.4 mm)', '8" (203.2 mm)', '16" (406.4 mm)', '750 mm', '1200 mm'),
      conveyorSpecOption('four_wheel_3', '9" / 18" (457.2 mm)', '9" (228.6 mm)', '18" (457.2 mm)', '900 mm', '1200 mm'),
      conveyorSpecOption('four_wheel_4', '10" / 20" (508 mm)', '10" (254 mm)', '20" (508 mm)', '1000 mm', '1200 mm'),
      conveyorSpecOption('four_wheel_5', '12" / 24" (609.6 mm)', '12" (304.8 mm)', '24" (609.6 mm)', '1200 mm', '1200 mm'),
    ],
  },
  {
    value: 'i_beam_x_348',
    label: 'I Beam X 348',
    subLabel: 'I-Beam Model X348 Overhead Conveyor',
    loadCapacityPerHook: '90 Kg',
    options: [
      conveyorSpecOption('i_beam_x_348_1', '3" / 6" (152.4 mm)', '3" (76.2 mm)', '6" (152.4 mm)', '457.2 mm', '1524 mm'),
      conveyorSpecOption('i_beam_x_348_2', '3" / 12" (304.8 mm)', '3" (76.2 mm)', '12" (304.8 mm)', '609.6 mm', '1524 mm'),
      conveyorSpecOption('i_beam_x_348_3', '3" / 18" (457.2 mm)', '3" (76.2 mm)', '18" (457.2 mm)', '609.6 mm', '2438 mm'),
      conveyorSpecOption('i_beam_x_348_4', '3" / 24" (609.6 mm)', '3" (76.2 mm)', '24" (609.6 mm)', '609.6 mm', '2438 mm'),
    ],
  },
  {
    value: 'i_beam_x_458',
    label: 'I Beam X 458',
    subLabel: 'I-Beam Model X458 Overhead Conveyor',
    loadCapacityPerHook: '180 Kg',
    options: [
      conveyorSpecOption('i_beam_x_458_1', '4" / 8" (203.2 mm)', '4" (101.6 mm)', '8" (203.2 mm)', '457.2 mm', '2438 mm'),
      conveyorSpecOption('i_beam_x_458_2', '4" / 16" (406.4 mm)', '4" (101.6 mm)', '16" (406.4 mm)', '609.6 mm', '2438 mm'),
      conveyorSpecOption('i_beam_x_458_3', '4" / 24" (609.6 mm)', '4" (101.6 mm)', '24" (609.6 mm)', '750 mm', '3048 mm'),
      conveyorSpecOption('i_beam_x_458_4', '4" / 32" (812.8 mm)', '4" (101.6 mm)', '32" (812.8 mm)', '914.4 mm', '3657 mm'),
    ],
  },
  {
    value: 'i_beam_x_678',
    label: 'I Beam X 678',
    subLabel: 'I-Beam Model X678 Overhead Conveyor',
    loadCapacityPerHook: '500 Kg',
    options: [
      conveyorSpecOption('i_beam_x_678_1', '6" / 12" (304.8 mm)', '6" (152.4 mm)', '12" (304.8 mm)', '914.4 mm', '3657 mm'),
      conveyorSpecOption('i_beam_x_678_2', '6" / 24" (609.6 mm)', '6" (152.4 mm)', '24" (609.6 mm)', '914.4 mm', '4572 mm'),
      conveyorSpecOption('i_beam_x_678_3', '6" / 36" (914.4 mm)', '6" (152.4 mm)', '36" (914.4 mm)', '1219.2 mm', '6096 mm'),
    ],
  },
];

// const boothVariantOptions = [
//   { value: 'booth_with_reciprocator_cutout', label: 'Booth With Reciprocator Cutout' },
//   { value: 'four_manual_cutouts', label: 'Booth Four Manual Cutouts' },
//   { value: 'two_manual_cutouts', label: 'Booth Two Manual Cutouts' },
//   { value: 'single_operator', label: 'Single Operator Booth' },
//   { value: 'jumbo', label: 'Jumbo Booth' },
//   { value: 'cartridge_filter_booth', label: 'Cartridge Filter Booth' },
// ];

const boothOptions = [
  { value: 'booth_with_reciprocator_cutout', label: 'Booth With Reciprocator Cutout' },
  { value: 'four_manual_cutouts', label: 'Booth Four Manual Cutouts' },
  { value: 'two_manual_cutouts', label: 'Booth Two Manual Cutouts' },
  { value: 'single_operator', label: 'Single Operator Booth' },
  { value: 'jumbo', label: 'Jumbo Booth' },
  { value: 'cartridge_filter_booth', label: 'Cartridge Filter Booth' },
  { value: 'plastic', label: 'Plastic Booth' },
  { value: 'car_painting_booth', label: 'Car Painting Booth' },
  { value: 'liquid_painting_booth', label: 'Liquid Painting Booth' },
];

const ptLineOptions = [
  { value: 'spray_pt_line', label: 'Spray PT Line' },
  { value: 'dip_pt_line', label: 'Dip PT Line' },
];

const ptLineMaterialOptions = [
  { value: 'ms', label: 'MS' },
  { value: 'ss', label: 'SS' },
];

const powderTransferSystemMaterialOptions = [
  { value: 'ms', label: 'MS' },
  { value: 'ss', label: 'SS' },
];

const componentMaterialOptions = [
  { value: 'gi', label: 'GI', minTank: 2, maxTank: 14 },
  { value: 'ms', label: 'MS', minTank: 2, maxTank: 14 },
  { value: 'al', label: 'AL', minTank: 2, maxTank: 8 },
];

const dustCollectorOptions = [
  '3000', '6000', '8000', '10000', '12000', '16000', '20000', '24000', '28000', '32000',
].map((n) => ({ value: `dc_${n}`, label: `DC ${n}` }));

const hopperOptions = [
  { value: 'hf_100', label: 'HF 100 Powder Hopper', injectorCount: 1 },
  { value: 'hf_150', label: 'HF 150 Powder Hopper', injectorCount: 2 },
  { value: 'hf_200', label: 'HF 200 Powder Hopper', injectorCount: 3 },
];

const hopperMaterialOptions = [
  { value: 'gi', label: 'GI', minTank: 2, maxTank: 14 },
  { value: 'ms', label: 'MS', minTank: 2, maxTank: 14 },
];

const reciprocatorOptions = [
  { value: 'za_07', label: 'ZA 07 Reciprocator' },
  { value: 'za_10', label: 'ZA 10 Reciprocator' },
];

const manualGunOptions = [
  { value: 'gema_manual_gun', label: 'Gema Manual Gun' },
  //   { value: 'other_manual_gun', label: 'Other Manual Gun' },
];

/**
 * Keys for technical spec tables. Use with getTechnicalTable(specs, productKey) to show table in form.
 * @see technical_specification.js – getTechnicalTable, technicalSpecTables
 */
export const productTechnicalSpecKeys = {
  oven: 'powder_coating_curing_oven',
  conveyor: 'over_head_conveyor',
  booth: 'recovery_booth_pollution_free',
  pt_line: 'pre_treatment_process',
  powder_transfer_system: 'powder_transfer_system',
  manual_gun: 'manual_gun',
  dust_collector: 'dust_collector',
  reciprocator: null,
};

/** Form/dropdown config by section. Use .id for form keys, .options for select/pickers. .technicalSpecKey for table. */
export const quotationFormConfig = {
  oven: {
    id: 'oven',
    label: 'Oven Type',
    technicalSpecKey: productTechnicalSpecKeys.oven,
    options: ovenOptions,
  },
  conveyor: {
    id: 'conveyor',
    label: 'Conveyor',
    technicalSpecKey: productTechnicalSpecKeys.conveyor,
    options: conveyorOptions,
  },
  booth: {
    id: 'booth',
    label: 'Booth',
    technicalSpecKey: productTechnicalSpecKeys.booth,
    options: boothOptions,
  },
  ptLine: {
    id: 'pt_line',
    label: 'PT Line',
    technicalSpecKey: productTechnicalSpecKeys.pt_line,
    options: ptLineOptions,
  },
  ptLineMaterial: {
    id: 'pt_line_material',
    label: 'PT Line Material',
    technicalSpecKey: null,
    options: ptLineMaterialOptions,
  },
  componentMaterial: {
    id: 'component_material',
    label: 'Component Material',
    technicalSpecKey: null,
    options: componentMaterialOptions,
  },
  dustCollector: {
    id: 'dust_collector',
    label: 'Dust Collector',
    technicalSpecKey: productTechnicalSpecKeys.dust_collector,
    options: dustCollectorOptions,
  },
  hopper: {
    id: 'hopper',
    label: 'Powder Hopper',
    technicalSpecKey: null,
    options: hopperOptions,
  },
  hopperMaterial: {
    id: 'hopper_material',
    label: 'Hopper Material',
    technicalSpecKey: null,
    options: hopperMaterialOptions,
  },
  reciprocator: {
    id: 'reciprocator',
    label: 'Reciprocator',
    technicalSpecKey: productTechnicalSpecKeys.reciprocator,
    options: reciprocatorOptions,
  },
  manualGun: {
    id: 'manual_gun',
    label: 'Manual Gun',
    technicalSpecKey: productTechnicalSpecKeys.manual_gun,
    options: manualGunOptions,
  },
  powderTransferSystem: {
    id: 'powder_transfer_system',
    label: 'Powder Transfer System',
    technicalSpecKey: productTechnicalSpecKeys.powder_transfer_system,
    options: powderTransferSystemMaterialOptions,
  },
  gemaAutomaticSystem: {
    id: 'gema_automatic_system',
    label: 'Gema Automatic System',
    technicalSpecKey: null,
    options: getGemaComponentOptions(),
  },
  cyclone:{
    id: 'cyclone',
    label: 'Cyclone',
    technicalSpecKey: null,
    options: [],
  }
};

/**
 * Main product config – use first to choose product type, then use quotationFormConfig[option.configKey].
 * Each option has configKey (form config) and technicalSpecKey (table key for getTechnicalTable).
 */
export const mainProductConfig = {
  id: 'main_product',
  label: 'Product',
  options: [
    { value: 'oven', label: 'Oven', configKey: 'oven', technicalSpecKey: 'powder_coating_curing_oven' },
    { value: 'conveyor', label: 'Conveyor', configKey: 'conveyor', technicalSpecKey: 'over_head_conveyor' },
    { value: 'booth', label: 'Booth', configKey: 'booth', technicalSpecKey: 'recovery_booth_pollution_free' },
    { value: 'pt_line', label: 'PT Line', configKey: 'ptLine', technicalSpecKey: 'pre_treatment_process' },
    { value: 'powder_transfer_system', label: 'Powder Transfer System', configKey: 'powderTransferSystem', technicalSpecKey: 'powder_transfer_system' },
    { value: 'dust_collector', label: 'Dust Collector', configKey: 'dustCollector', technicalSpecKey: 'dust_collector' },
    { value: 'reciprocator', label: 'Reciprocator', configKey: 'reciprocator', technicalSpecKey: null },
    { value: 'manual_gun', label: 'Manual Gun', configKey: 'manualGun', technicalSpecKey: 'manual_gun' },
    { value: 'gema_automatic_system', label: 'Gema Automatic System', configKey: 'gemaAutomaticSystem', technicalSpecKey: null },
      { value: 'cyclone', label: 'Cyclone', configKey: 'cyclone', technicalSpecKey: null },
  ],
};

/** Tank steps count (e.g. for PT). Define steps as needed. */
export const tankSteps = []; // e.g. 2–14 steps

/** Re-export table helpers: pass technicalSpecification + product key to get { label, headers, data } for TechnicalDataTable. */
export { getTechnicalTable, technicalSpecTables, getGemaComponentOptions } from './technical_specification.js';

export default quotationFormConfig;
